import React, { useState, useEffect, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SettingsIcon from "@mui/icons-material/Settings";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import CampaignIcon from "@mui/icons-material/Campaign";
import { styled } from "@mui/material/styles";
import { fetchUserReleaseNotifications, markReleaseNotificationRead } from "../../api/accountServices";

const CustomMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    width: "450px",
    maxWidth: "95vw",
    maxHeight: "520px",
    borderRadius: "12px",
    boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
  },
  "& .MuiList-root": {
    padding: 0,
  },
}));

const SpinnerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100px",
}));

const NoNotificationsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  textAlign: "center",
}));

export interface NotificationItem {
  id: number;
  message?: string;
  read?: boolean;
  is_read?: boolean;
  release?: {
    id: number;
    version: string;
    title: string;
    description: string;
    created_at: string;
  };
  created_at?: string;
}

const stripHtml = (html: string) => {
  if (!html) return "";
  const formatted = html
    .replace(/<\/(p|div|li|h[1-6])>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ");
  const tmp = document.createElement("DIV");
  tmp.innerHTML = formatted;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

const NotificationBell: React.FC<{
  unreadCount?: number;
  notifications?: NotificationItem[];
  loading?: boolean;
  onSettingsClick?: () => void;
  color?: string;
}> = ({
  unreadCount: propUnreadCount,
  notifications: propNotifications,
  loading: propLoading,
  onSettingsClick,
  color = "black",
}) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [releaseNotifications, setReleaseNotifications] = useState<NotificationItem[]>([]);
    const [backendUnreadCount, setBackendUnreadCount] = useState(0);
    const [showAllInMenu, setShowAllInMenu] = useState(false);
    const [loading, setLoading] = useState(false);

    const loadNotifications = async () => {
      try {
        setLoading(true);
        const res = await fetchUserReleaseNotifications();
        if (res && res.notifications) {
          setReleaseNotifications(res.notifications);
          setBackendUnreadCount(res.unread_count ?? 0);
        }
      } catch (err) {
        console.error("Failed to load release notifications", err);
      } finally {
        setLoading(false);
      }
    };

    const handleMarkAsRead = async (id: number) => {
      try {
        await markReleaseNotificationRead(id);
        setReleaseNotifications((prev) =>
          prev.map((item) => (item.id === id ? { ...item, is_read: true } : item))
        );
        setBackendUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error("Failed to mark notification as read", err);
      }
    };

    const handleMarkAllRead = async () => {
      try {
        await markReleaseNotificationRead();
        setReleaseNotifications((prev) =>
          prev.map((item) => ({ ...item, is_read: true }))
        );
        setBackendUnreadCount(0);
      } catch (err) {
        console.error("Failed to mark all as read", err);
      }
    };

    useEffect(() => {
      loadNotifications();
    }, []);

    const handleBellClick = (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      loadNotifications();
    };

    const handleClose = () => {
      setAnchorEl(null);
    };


    const totalUnread = (propUnreadCount ?? 0) + backendUnreadCount;
    const rawNotifications = [...releaseNotifications, ...(propNotifications || []),];
    const displayedNotifications = showAllInMenu ? rawNotifications : rawNotifications.filter((item) => !(item.is_read ?? item.read));

    const open = Boolean(anchorEl);
    const id = open ? "notification-menu" : undefined;

    return (
      <Box sx={{ position: "relative", display: "inline-block" }}>
        <IconButton
          onClick={handleBellClick}
          sx={{
            color,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <Badge badgeContent={totalUnread} color="error">
            {totalUnread > 0 ? (
              <NotificationsActiveIcon fontSize="inherit" />
            ) : (
              <NotificationsNoneIcon fontSize="inherit" />
            )}
          </Badge>
        </IconButton>

        <CustomMenu id={id} anchorEl={anchorEl} open={open} onClose={handleClose}>
          <Box
            sx={{
              padding: "14px 16px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem", whiteSpace: "nowrap" }}>
                Notifications
              </Typography>
              {totalUnread > 0 && (
                <Chip
                  label={`${totalUnread} New`}
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {backendUnreadCount > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<MarkEmailReadIcon fontSize="small" />}
                  onClick={handleMarkAllRead}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "6px",
                    boxShadow: "none",
                  }}
                >
                  Mark all read
                </Button>
              )}
              {onSettingsClick && (
                <IconButton size="small" onClick={onSettingsClick} sx={{ color: "#64748b" }}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Scrollable Notifications Area (Thin Scrollbar) */}
          <Box
            sx={{
              maxHeight: "380px",
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarWidth: "thin",
              scrollbarColor: "#cbd5e1 transparent",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#94a3b8",
                },
              },
            }}
          >
            {loading || propLoading ? (
              <SpinnerContainer>
                <CircularProgress size={30} />
              </SpinnerContainer>
            ) : displayedNotifications.length > 0 ? (
              displayedNotifications.map((notif) => {
                const isRead = notif.is_read ?? notif.read ?? false;
                const release = notif.release;

                return (
                  <React.Fragment key={notif.id}>
                    <MenuItem
                      onClick={() => {
                        if (!isRead) {
                          handleMarkAsRead(notif.id);
                        }
                        if (notif.release) {
                          handleClose();
                          navigate("/release-notes");
                        }
                      }}
                      sx={{
                        padding: "10px 16px",
                        minHeight: "auto !important",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        borderBottom: "1px solid #f1f5f9",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                        backgroundColor: isRead ? "#ffffff" : "#f0f7ff",
                        "&:hover": {
                          backgroundColor: isRead ? "#f8fafc" : "#e6f0fa",
                          "& .view-more-text": {
                            color: "#0284c7",
                          },
                        },
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      {release ? (
                        <CampaignIcon color="primary" sx={{ mt: 0.2, fontSize: 18, flexShrink: 0 }} />
                      ) : (
                        <NotificationsNoneIcon color="action" sx={{ mt: 0.2, fontSize: 18, flexShrink: 0 }} />
                      )}

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.1, gap: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: "#0f172a",
                              fontSize: "0.825rem",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {release ? release.title : 'Notification'}
                          </Typography>
                          {!isRead && (
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                flexShrink: 0,
                                borderRadius: "50%",
                                backgroundColor: "#d32f2f",
                              }}
                            />
                          )}
                        </Box>

                        {release ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: "0.75rem",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {stripHtml(release.description)}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.825rem",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {notif.message}
                          </Typography>
                        )}

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.3 }}>
                          <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.725rem" }}>
                            {notif.created_at
                              ? new Date(notif.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                              : isRead
                                ? "Read"
                                : "Unread"}
                          </Typography>

                          {release && (
                            <Typography
                              className="view-more-text"
                              variant="caption"
                              sx={{
                                color: "#1976d2",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                transition: "color 0.2s ease",
                                "&:hover": {
                                  color: "#0369a1",
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              view more...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  </React.Fragment>
                );
              })
            ) : (
              <NoNotificationsContainer>
                <NotificationsNoneIcon sx={{ fontSize: 80, color }} />
                <Typography
                  variant="h6"
                  sx={{ marginTop: "16px", marginBottom: "8px" }}
                >
                  Your notifications live here
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Any notificaiton related to your clinic(s) will show here
                </Typography>
              </NoNotificationsContainer>
            )}
          </Box>

          {/* Footer Bar: Toggle Read */}
          <Box
            sx={{
              p: 1.2,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderTop: "1px solid #f0f0f0",
              bgcolor: "#f8fafc",
            }}
          >
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAllInMenu((prev) => !prev)}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: "#1976d2 !important",
                bgcolor: "transparent !important",
                boxShadow: "none !important",
                "&:hover": { bgcolor: "rgba(25, 118, 210, 0.08) !important" },
              }}
            >
              {showAllInMenu ? "Show unread only" : "Show read notifications"}
            </Button>
          </Box>
        </CustomMenu>
      </Box>
    );
  };

export default NotificationBell;
